// Vedic Samudrika Shastra & Palmistry Engine (हस्तरेखा शास्त्र)

export interface PalmMount {
  id: string;
  name: string;
  nameHi: string;
  location: string;
  locationHi: string;
  rulingPlanet: string;
  rulingPlanetHi: string;
  description: string;
  descriptionHi: string;
  developedSignificance: string;
  developedSignificanceHi: string;
  underdevelopedSignificance: string;
  underdevelopedSignificanceHi: string;
  auspiciousSigns: string[];
  remedies: string;
}

export interface PalmLine {
  id: string;
  name: string;
  nameHi: string;
  sanskritName: string;
  description: string;
  descriptionHi: string;
  variations: {
    label: string;
    labelHi: string;
    meaning: string;
    meaningHi: string;
  }[];
  keyIndicators: string[];
  keyIndicatorsHi: string[];
}

export interface SpecialPalmSign {
  id: string;
  name: string;
  nameHi: string;
  symbolName: string;
  significance: string;
  significanceHi: string;
  rarity: 'Common' | 'Rare' | 'Extremely Auspicious' | 'Divine Mark';
  mountLocations: string;
}

export const PALM_MOUNTS: PalmMount[] = [
  {
    id: 'jupiter',
    name: 'Mount of Jupiter (Guru Parvat)',
    nameHi: 'बृहस्पति पर्वत (गुरु पर्वत)',
    location: 'Under the Index finger (Tarjani)',
    locationHi: 'तर्जनी उंगली के मूल में स्थित',
    rulingPlanet: 'Jupiter (Brihaspati)',
    rulingPlanetHi: 'देवगुरु बृहस्पति',
    description: 'Governs ambition, leadership, religious piety, wisdom, honor, high administrative career, and benevolence.',
    descriptionHi: 'नेतृत्व क्षमता, आत्मसम्मान, धार्मिक आस्था, विद्या, उच्च पद और परोपकार का प्रतिनिधित्व करता है।',
    developedSignificance: 'High leadership capacity, prestigious reputation, generous heart, spiritual and material prosperity.',
    developedSignificanceHi: 'उत्कृष्ट नेतृत्व क्षमता, समाज में प्रतिष्ठा, उच्च प्रशासनिक योग एवं अध्यात्म में रुचि।',
    underdevelopedSignificance: 'Lack of self-confidence, timid nature, difficulties in gaining social status.',
    underdevelopedSignificanceHi: 'आत्मविश्वास की कमी, मान-सम्मान पाने में बाधा एवं संकोची स्वभाव।',
    auspiciousSigns: ['Cross (Happy Marriage)', 'Star (Sudden Elevation)', 'Trishul (Divine Honor)', 'Square (Protection from Scandal)'],
    remedies: 'Chant Om Gurave Namah, respect teachers/elders, apply saffron tilak, wear yellow.'
  },
  {
    id: 'saturn',
    name: 'Mount of Saturn (Shani Parvat)',
    nameHi: 'शनि पर्वत',
    location: 'Under the Middle finger (Madhyama)',
    locationHi: 'मध्यमा उंगली के मूल में स्थित',
    rulingPlanet: 'Saturn (Shani)',
    rulingPlanetHi: 'कर्मफलदाता शनि',
    description: 'Governs fate, patience, deep research, solitude, career endurance, engineering, judiciary, and occult wisdom.',
    descriptionHi: 'भाग्य, कर्म, अनुसंधान, रहस्य विद्या, न्याय, एकांतप्रियता एवं तकनीकी क्षेत्र का सूचक।',
    developedSignificance: 'Deep wisdom, balanced contemplation, perseverance, triumph over karmic struggles, enduring legacy.',
    developedSignificanceHi: 'गंभीर विचारशीलता, धैर्य, परिश्रम से बड़ी संपत्ति निर्माण और न्यायप्रियता।',
    underdevelopedSignificance: 'Pessimism, susceptibility to depression, feeling weighed down by heavy duties.',
    underdevelopedSignificanceHi: 'मानसिक तनाव, काम में विलंब, निराशावादी सोच।',
    auspiciousSigns: ['Trishul (Raja Yoga)', 'Single Vertical Line (Lucky Fate)', 'Square (Protection from accident)'],
    remedies: 'Feed birds and stray dogs on Saturday, chant Hanuman Chalisa, light mustard oil lamp under Peepal tree.'
  },
  {
    id: 'sun',
    name: 'Mount of Sun / Apollo (Surya Parvat)',
    nameHi: 'सूर्य पर्वत',
    location: 'Under the Ring finger (Anamika)',
    locationHi: 'अनामिका उंगली के मूल में स्थित',
    rulingPlanet: 'Sun (Surya)',
    rulingPlanetHi: 'सूर्य देव',
    description: 'Governs fame, government recognition, creative genius, vitality, charisma, artistic brilliance, and wealth.',
    descriptionHi: 'प्रसिद्धि, सरकारी सम्मान, कलात्मक प्रतिभा, यश, तेज और धन-ऐश्वर्य का केंद्र।',
    developedSignificance: 'Widespread fame, government honors, high artistic accomplishments, brilliant magnetic persona.',
    developedSignificanceHi: 'विशाल ख्याति, राजकृपा, कला व व्यापार में यश और निरंतर धन का आगमन।',
    underdevelopedSignificance: 'Hard work going uncredited, lack of spotlight, eye or bone weaknesses.',
    underdevelopedSignificanceHi: 'मेहनत का उचित श्रेय न मिलना, अपयश का भय, नेत्र व अस्थि कष्ट।',
    auspiciousSigns: ['Matsya / Fish (Global Wealth)', 'Star (Celebrity Status)', 'Lotus (Lakshmi Blessing)'],
    remedies: 'Offer Arghya (water) with copper vessel to Rising Sun, chant Gayatri Mantra, respect father.'
  },
  {
    id: 'mercury',
    name: 'Mount of Mercury (Budh Parvat)',
    nameHi: 'बुध पर्वत',
    location: 'Under the Little finger (Kanishtha)',
    locationHi: 'कनिष्ठिका (छोटी) उंगली के मूल में स्थित',
    rulingPlanet: 'Mercury (Budha)',
    rulingPlanetHi: 'बुध ग्रह',
    description: 'Governs eloquence, commercial enterprise, scientific intellect, mathematical acumen, wit, and trade.',
    descriptionHi: 'वाक्पटुता, व्यापारिक चातुर्य, त्वरित बुद्धि, गणित, संचार व कूटनीति का स्वामी।',
    developedSignificance: 'Exceptional business success, persuasive speech, multiple income streams, financial foresight.',
    developedSignificanceHi: 'सफल व्यापारी, आकर्षक वाणी, त्वरित निर्णय और विज्ञान व शेयर बाजार में लाभ।',
    underdevelopedSignificance: 'Communication anxiety, business losses, hesitation in negotiation.',
    underdevelopedSignificanceHi: 'संवाद में संकोच, व्यापार में धोखा, निर्णय में दुविधा।',
    auspiciousSigns: ['Medical Stigmata (Healing Touch)', 'Trident', 'Double Line (Multi-Business)'],
    remedies: 'Feed green grass/spinach to cows on Wednesday, chant Om Budhaya Namah, wear green hues.'
  },
  {
    id: 'venus',
    name: 'Mount of Venus (Shukra Parvat)',
    nameHi: 'शुक्र पर्वत',
    location: 'At the base of the Thumb, enclosed by Life Line',
    locationHi: 'अंगूठे के मूल में, जीवन रेखा से घिरा हुआ भाग',
    rulingPlanet: 'Venus (Shukra)',
    rulingPlanetHi: 'दैत्यगुरु शुक्र',
    description: 'Governs romantic passions, luxury, beauty, vehicles, stamina, love for arts, elegance, and vitality.',
    descriptionHi: 'प्रेम, सुख-साधन, वाहन सुख, सौंदर्य, कामकला, आकर्षण एवं जीवन ऊर्जा का आधार।',
    developedSignificance: 'Rich lifestyle, romantic bliss, abundant comforts, physical vitality, artistic refinement.',
    developedSignificanceHi: 'समस्त भौतिक सुख, सुंदर घर-वाहन, सुखी वैवाहिक जीवन और दीर्घकालिक यौवन।',
    underdevelopedSignificance: 'Cold demeanor, lack of zest for life, ascetic indifference to comfort.',
    underdevelopedSignificanceHi: 'सुखों का अभाव, वैवाहिक जीवन में नीरसता, शारीरिक कमजोरी।',
    auspiciousSigns: ['Square (Safe from scandels)', 'Star (Artistic Breakthrough)', 'Clear Grids'],
    remedies: 'Chant Om Shukraya Namah, donate white sweets on Friday, wear pleasant perfume.'
  },
  {
    id: 'moon',
    name: 'Mount of Moon (Chandra Parvat)',
    nameHi: 'चंद्र पर्वत',
    location: 'Lower percussion of palm opposite to Mount of Venus',
    locationHi: 'शुक्र पर्वत के विपरीत, हथेली के निचले बाहरी भाग में',
    rulingPlanet: 'Moon (Chandra)',
    rulingPlanetHi: 'चंद्र देव',
    description: 'Governs deep imagination, subconscious mind, psychic intuition, overseas travel, water voyages, and writing.',
    descriptionHi: 'कल्पना शक्ति, अवचेतन मन, विदेश यात्रा, समुद्र पार व्यापार, लेखन और अंतर्ज्ञान।',
    developedSignificance: 'Frequent overseas success, rich poetic/literary talent, psychic intuition, calm emotional peace.',
    developedSignificanceHi: 'विदेश यात्राएं व विदेश से धन लाभ, उत्तम लेखक व कवि, अंतर्मुखी शांति।',
    underdevelopedSignificance: 'Restless mind, homesickness, irrational phobias, digestive instability.',
    underdevelopedSignificanceHi: 'मानसिक बेचैनी, यात्रा में बाधा, अनिद्रा एवं अनावश्यक चिंता।',
    auspiciousSigns: ['Triangle (Psychic Insight)', 'Fish (Wealth from Foreign Lands)', 'Star (Literary Fame)'],
    remedies: 'Respect mother, drink water in silver glass, offer milk to Shivling on Mondays.'
  },
  {
    id: 'upper_mars',
    name: 'Upper Mars (Mental Courage / Raksha Mangal)',
    nameHi: 'उच्च मंगल (मानसिक साहस)',
    location: 'Between Mercury mount and Moon mount on the edge',
    locationHi: 'बुध और चंद्र पर्वत के मध्य, हथेली के बाहरी किनारे पर',
    rulingPlanet: 'Mars (Mangal)',
    rulingPlanetHi: 'मंगल देव',
    description: 'Governs moral courage, self-control, composure during crises, defensive resilience, and stoicism.',
    descriptionHi: 'मानसिक दृढ़ता, संकट में धैर्य, आत्म-नियंत्रण और विपरीत परिस्थितियों से मुकाबला करने की शक्ति।',
    developedSignificance: 'Unshakable calm under intense pressure, coolness in war or crisis, fierce moral spine.',
    developedSignificanceHi: 'संकट में अडिग धैर्य, शत्रुओं पर विजय और अटूट मानसिक संतुलन।',
    underdevelopedSignificance: 'Surrendering quickly to pressure, panic during emergencies.',
    underdevelopedSignificanceHi: 'दबाव में घबरा जाना, मानसिक भय।',
    auspiciousSigns: ['Square (Protection in disputes)', 'Cross'],
    remedies: 'Recite Hanuman Chalisa, exercise daily, consume jaggery on Tuesday.'
  },
  {
    id: 'lower_mars',
    name: 'Lower Mars (Physical Valor / Agni Mangal)',
    nameHi: 'निम्न मंगल (शारीरिक पराक्रम)',
    location: 'Inside the curve of Life Line, above Mount of Venus',
    locationHi: 'जीवन रेखा के अंदर, शुक्र पर्वत के ठीक ऊपर',
    rulingPlanet: 'Mars (Mangal)',
    rulingPlanetHi: 'मंगल देव',
    description: 'Governs aggressive drive, physical bravery, sports prowess, military spirit, and land ownership.',
    descriptionHi: 'शारीरिक बल, निडरता, खेलकूद, सेना-पुलिस में सफलता और भूमि-भवन प्राप्ति।',
    developedSignificance: 'Fearless bravery, muscular stamina, rapid athletic recovery, land acquisitions.',
    developedSignificanceHi: 'असीम शारीरिक शक्ति, निडर व्यक्तित्व और अचल संपत्ति का स्वामी।',
    underdevelopedSignificance: 'Physical passivity, fear of confrontation, hesitation.',
    underdevelopedSignificanceHi: 'कायरता, शारीरिक दुर्बलता।',
    auspiciousSigns: ['Mars Line / Sister Line (Divine Protection)'],
    remedies: 'Donate blood if healthy, chant Om Angarakaya Namah.'
  }
];

export const PALM_LINES: PalmLine[] = [
  {
    id: 'life_line',
    name: 'Life Line (Jeevan Rekha)',
    nameHi: 'जीवन रेखा (पितृ रेखा / आयु रेखा)',
    sanskritName: 'आयु रेखा / जीवन रेखा (Ayur Rekha)',
    description: 'Encircles the Mount of Venus, mapping vital life force (Prana), physical constitution, longevity, immune vitality, and monumental life milestones.',
    descriptionHi: 'शुक्र पर्वत को घेरती हुई कलाई की ओर जाने वाली रेखा। यह प्राण शक्ति, शारीरिक स्वास्थ्य, आयु और जीवन के प्रमुख परिवर्तनों का ज्ञान कराती है।',
    variations: [
      {
        label: 'Deep, Clear & Wide Arch around Venus',
        labelHi: 'गहरी, स्पष्ट एवं शुक्र पर्वत को पूरा घेरती हुई',
        meaning: 'Robust vitality, immense Pranic stamina, joyful longevity, and strong immunity against ailments.',
        meaningHi: 'दीर्घायु, उत्तम स्वास्थ्य, असीम जीवन ऊर्जा और रोगों से लड़ने की अद्भुत क्षमता।'
      },
      {
        label: 'Straight & Hugging Close to Thumb',
        labelHi: 'अंगूठे के पास से सीधी उतरती हुई',
        meaning: 'Prone to low stamina, prefers indoor quietness, cautious with physical exertion.',
        meaningHi: 'कम शारीरिक सहनशक्ति, शांत व एकांत वातावरण प्रिय, संकोची स्वभाव।'
      },
      {
        label: 'Upward Branches shooting towards Jupiter Mount',
        labelHi: 'बृहस्पति पर्वत की ओर उठती हुई शाखाएं',
        meaning: 'Promotes tremendous career elevation, ambition fulfilled, and self-made triumphs in youth.',
        meaningHi: 'प्रारंभिक जीवन में ही भारी सफलता, उच्च पद और महत्वाकांक्षाओं की पूर्ति।'
      },
      {
        label: 'Chained or Islanded Line at Start',
        labelHi: 'प्रारंभ में जंजीरदार अथवा द्वीप युक्त',
        meaning: 'Delicate childhood constitution followed by strengthening vitality after teenage years.',
        meaningHi: 'बाल्यावस्था में स्वास्थ्य समस्याएं, जो युवावस्था के बाद पूर्णतः ठीक हो जाती हैं।'
      },
      {
        label: 'Double Life Line (Mars Line / Angarak Rekha)',
        labelHi: 'समानांतर मंगल रेखा (देवदूत रेखा)',
        meaning: 'Divine armor: Complete protection from fatal hazards, accidents, and unseen enemies throughout life.',
        meaningHi: 'अंगारक/मंगल रक्षा कवच: दुर्घटनाओं व गंभीर संकटों से अदृश्य ईश्वरीय सुरक्षा।'
      }
    ],
    keyIndicators: ['Vital Force (Prana)', 'Longevity Milestones', 'Health Resilience', 'Major Relocations'],
    keyIndicatorsHi: ['प्राण शक्ति', 'आयु काल', 'रोग प्रतिरोधक क्षमता', 'स्थान परिवर्तन']
  },
  {
    id: 'head_line',
    name: 'Head Line (Mastishk Rekha / Matri Rekha)',
    nameHi: 'मस्तिष्क रेखा (ज्ञान व बुद्धि रेखा)',
    sanskritName: 'मातृ रेखा / ज्ञान रेखा (Matri Rekha)',
    description: 'Traverses the palm horizontally, reflecting psychological focus, intellectual depth, mental resilience, creativity, and cognitive strategy.',
    descriptionHi: 'हथेली के मध्य से गुजरने वाली रेखा जो बौद्धिक क्षमता, निर्णय शक्ति, मानसिक संतुलन, शोध और चिंतन की दिशा दर्शाती है।',
    variations: [
      {
        label: 'Straight & Clear across to Upper Mars',
        labelHi: 'सीधी मंगल पर्वत तक जाने वाली रेखा',
        meaning: 'Highly logical, practical, analytical, objective, and excels in finance, law, engineering, and data.',
        meaningHi: 'व्यावहारिक, तार्किक, तीक्ष्ण बुद्धि, वित्तीय मामलों में दक्ष और यथार्थवादी।'
      },
      {
        label: 'Gently Sloping down to Mount of Moon',
        labelHi: 'चंद्र पर्वत की ओर हल्का झुकाव लिए',
        meaning: 'Brilliant creative imagination, artistic flair, psychological insight, intuitive writer or designer.',
        meaningHi: 'रचनात्मक प्रतिभा, कलात्मक सुरुचि, लेखक, मनोवैज्ञानिक और उत्कृष्ट कल्पना शक्ति।'
      },
      {
        label: 'Writer\'s Fork (Divided into two branches at end)',
        labelHi: 'अंत में दो भागों में बंटी (लेखक द्विशाखा)',
        meaning: 'Mastery of both logic and artistic intuition: Rare gift for converting creative ideas into lucrative businesses.',
        meaningHi: 'तर्क और कल्पना का अद्भुत संगम: विचारों को धन में बदलने की विलक्षण क्षमता।'
      },
      {
        label: 'Long Line touching palm edge',
        labelHi: 'हथेली के आर-पार जाने वाली अत्यंत लंबी रेखा',
        meaning: 'Deep philosophical and analytical thinker who refuses superficial answers; perpetual scholar.',
        meaningHi: 'गहन शोधकर्ता, विषय की तह तक जाने वाला, गंभीर और अत्यंत मेधावी।'
      }
    ],
    keyIndicators: ['Intellectual Style', 'Memory & Focus', 'Strategic Thinking', 'Stress Coping'],
    keyIndicatorsHi: ['बौद्धिक शैली', 'स्मृति व एकाग्रता', 'रणनीतिक सोच', 'मानसिक सहनशीलता']
  },
  {
    id: 'heart_line',
    name: 'Heart Line (Hriday Rekha / Ayur Rekha)',
    nameHi: 'हृदय रेखा (प्रेम, संवेदना व स्वास्थ्य)',
    sanskritName: 'हृदय रेखा (Hridaya Rekha)',
    description: 'Runs along the upper palm below the fingers, governing emotional temper, romantic fidelity, devotion, compassion, and physical heart wellness.',
    descriptionHi: 'उंगलियों के नीचे स्थित रेखा जो प्रेम, भावनाएं, दांपत्य जीवन, दयालुता और शारीरिक हृदय के स्वास्थ्य को दर्शाती है।',
    variations: [
      {
        label: 'Terminating on Mount of Jupiter (Guru Parvat)',
        labelHi: 'बृहस्पति पर्वत पर समाप्त होने वाली',
        meaning: 'Idealistic, deeply loyal in love, noble character, seeks spiritual soulmate, respected by partner.',
        meaningHi: 'आदर्शवादी प्रेमी, निष्ठावान जीवनसाथी, उच्च नैतिक मूल्य और सुखी वैवाहिक जीवन।'
      },
      {
        label: 'Terminating between Jupiter & Saturn fingers',
        labelHi: 'तर्जनी और मध्यमा के मध्य समाप्त होने वाली',
        meaning: 'The Golden Mean: Perfectly balanced between emotional warmth and practical reality; harmonious marriage.',
        meaningHi: 'सर्वश्रेष्ठ संतुलन: प्रेम और व्यावहारिकता का उत्तम सामंजस्य, स्थिर दांपत्य सुख।'
      },
      {
        label: 'Terminating on Mount of Saturn (Shani Parvat)',
        labelHi: 'शनि पर्वत पर समाप्त होने वाली',
        meaning: 'Reserved, practical in affection, guards emotions closely, prioritizes security before romance.',
        meaningHi: 'भावुकता से परे, व्यावहारिक दृष्टिकोण, सोच-समझकर संबंध बनाने वाला।'
      },
      {
        label: 'Trident (Trishul) at the End',
        labelHi: 'अंत में त्रिशूल जैसी आकृति',
        meaning: 'Vedic Raja Yoga: Blessed with tremendous wealth, immense social respect, noble spouse, and divine grace.',
        meaningHi: 'महा-राजयोग: अपार धन, सुसंस्कृत जीवनसाथी, समाज में उच्च सम्मान व ईश्वरीय कृपा।'
      }
    ],
    keyIndicators: ['Romantic Loyalty', 'Emotional EQ', 'Compassion', 'Cardiovascular Wellness'],
    keyIndicatorsHi: ['प्रेम में निष्ठा', 'भावनात्मक परिपक्वता', 'सहानुभूति', 'हृदय स्वास्थ्य']
  },
  {
    id: 'fate_line',
    name: 'Fate / Destiny Line (Bhagya Rekha)',
    nameHi: 'भाग्य रेखा (कर्म रेखा / शनि रेखा)',
    sanskritName: 'उर्ध्व रेखा / भाग्य रेखा (Urdhva Rekha)',
    description: 'Vertical line ascending toward the Mount of Saturn, revealing career trajectory, public success, financial windfalls, and karmic destiny.',
    descriptionHi: 'कलाई या हथेली के मध्य से शनि पर्वत की ओर उठने वाली रेखा, जो आजीविका, धन, भाग्य वृद्धि और करियर के उत्थान को दर्शाती है।',
    variations: [
      {
        label: 'Rising Directly from Wrist / Manibandha',
        labelHi: 'मणिबंध (कलाई) से सीधे आरंभ होकर शनि तक',
        meaning: 'Early career clarity, disciplined self-made fortune, steady rise in wealth from youth onwards.',
        meaningHi: 'प्रारंभिक आयु से ही भाग्य का साथ, स्वतः अर्जित धन-संपत्ति और निरंतर उन्नति।'
      },
      {
        label: 'Rising from Mount of Moon (Chandra Parvat)',
        labelHi: 'चंद्र पर्वत से आरंभ होने वाली',
        meaning: 'Fame and wealth through public popularity, partners, entertainment, overseas connections, or opposite sex.',
        meaningHi: 'जनता, प्रशंसकों, जीवनसाथी व विदेश के सहयोग से अपार सफलता व ख्याति।'
      },
      {
        label: 'Rising from Life Line (Jeevan Rekha)',
        labelHi: 'जीवन रेखा से निकलकर ऊपर उठने वाली',
        meaning: 'Completely self-made individual who builds an empire strictly through personal grit without inherited favors.',
        meaningHi: 'पूर्णतः स्व-निर्मित व्यक्तित्व, जिसने अपनी मेहनत से शून्य से साम्राज्य खड़ा किया।'
      },
      {
        label: 'Double Fate Line (Parallel Bhagya Rekha)',
        labelHi: 'दोहरी समानांतर भाग्य रेखा',
        meaning: 'Dual simultaneous flourishing careers, massive wealth accumulation, continuous secondary income.',
        meaningHi: 'एक साथ दो सफल व्यवसाय/करियर, निरंतर प्रचुर धन लाभ व संपन्नता।'
      }
    ],
    keyIndicators: ['Career Milestones', 'Wealth Inflows', 'Public Recognition', 'Self-Reliance'],
    keyIndicatorsHi: ['करियर उत्थान', 'धन आगमन', 'सामाजिक प्रतिष्ठा', 'आत्मनिर्भरता']
  },
  {
    id: 'sun_line',
    name: 'Sun / Apollo Line (Surya Rekha / Yash Rekha)',
    nameHi: 'सूर्य रेखा (यश रेखा / कीर्ति रेखा)',
    sanskritName: 'सूर्य रेखा / कीर्ति रेखा (Surya Rekha)',
    description: 'Runs vertically toward the Mount of Sun, indicating celebrity status, artistic distinction, government contracts, and golden reputation.',
    descriptionHi: 'अनामिका उंगली (सूर्य पर्वत) की ओर जाने वाली रेखा जो जीवन में यश, उच्च पद, सरकारी सम्मान और विशेष पहचान दिलाती है।',
    variations: [
      {
        label: 'Deep, unbroken and long to palm center',
        labelHi: 'गहरी, स्पष्ट और निर्दोष सूर्य रेखा',
        meaning: 'Celebrity status, national/international awards, undisputed mastery in chosen profession.',
        meaningHi: 'उच्च कोटि की ख्याति, पुरस्कार, सरकारी सम्मान और समाज में अमिट छाप।'
      },
      {
        label: 'Forked into Trident on Sun Mount',
        labelHi: 'सूर्य पर्वत पर त्रिशूल जैसी शाखा',
        meaning: 'Supreme brilliance in arts, sciences or public leadership; massive prosperity and legacy.',
        meaningHi: 'कला, शासन या व्यापार में सर्वोच्च सफलता व अमर कीर्ति।'
      }
    ],
    keyIndicators: ['Fame & Glory', 'Government Favor', 'Artistic Mastery', 'Public Legacy'],
    keyIndicatorsHi: ['यश व कीर्ति', 'राजकीय सम्मान', 'कलात्मक महारत', 'प्रतिष्ठा']
  },
  {
    id: 'mercury_line',
    name: 'Mercury Line (Health / Business Line - Budh Rekha)',
    nameHi: 'बुध रेखा (स्वास्थ्य व व्यापार रेखा)',
    sanskritName: 'आरोग्य रेखा / व्यापार रेखा (Swasthya Rekha)',
    description: 'Runs toward the Mount of Mercury, indicating digestive resilience, nervous vitality, commercial instincts, and communication acumen.',
    descriptionHi: 'कनिष्ठिका (बुध पर्वत) की ओर जाने वाली रेखा जो पाचन स्वास्थ्य, व्यापारिक दूरदर्शिता और वाकपटुता को दर्शाती है।',
    variations: [
      {
        label: 'Clear, deep and straight',
        labelHi: 'स्पष्ट, सीधी और अटूट',
        meaning: 'Flawless digestive vitality, sharp commercial intelligence, lucrative investments.',
        meaningHi: 'उत्कृष्ट स्वास्थ्य, तीव्र व्यापारिक बुद्धि और निरंतर धन लाभ।'
      },
      {
        label: 'Absence of Mercury Line',
        labelHi: 'हथेली में बुध रेखा का न होना',
        meaning: 'Vedic palmistry considers absence of this line very auspicious: indicates naturally robust, problem-free constitution!',
        meaningHi: 'सामुद्रिक शास्त्र में इसका न होना अत्यंत शुभ माना जाता है - व्यक्ति स्वाभाविक रूप से स्वस्थ रहता है।'
      }
    ],
    keyIndicators: ['Business Acumen', 'Digestive Health', 'Commercial Eloquence', 'Intuition'],
    keyIndicatorsHi: ['व्यापार कौशल', 'पाचन शक्ति', 'वाक चातुर्य', 'व्यापारिक लाभ']
  },
  {
    id: 'marriage_line',
    name: 'Marriage / Union Line (Vivah Rekha)',
    nameHi: 'विवाह रेखा (संबंध व प्रणय रेखा)',
    sanskritName: 'विवाह रेखा (Vivaha Rekha)',
    description: 'Located on the percussion of palm between base of Mercury finger and Heart Line, indicating deep romantic commitments and marriage timing.',
    descriptionHi: 'कनिष्ठिका उंगली और हृदय रेखा के मध्य हथेली के किनारे पर स्थित क्षैतिज रेखाएं, जो विवाह और प्रगाढ़ प्रेम संबंधों को दर्शाती हैं।',
    variations: [
      {
        label: 'Single, deep, straight & clean line',
        labelHi: 'एकल, गहरी और सीधी रेखा',
        meaning: 'Lifelong blissful, loyal marriage with deep mutual devotion and prosperity.',
        meaningHi: 'आजीवन सुखद, निष्ठावान और समृद्ध वैवाहिक जीवन।'
      },
      {
        label: 'Curving upward towards fingers',
        labelHi: 'ऊपर उंगली की ओर मुड़ती हुई',
        meaning: 'Highly selective, independent, or delay in finding the matching spiritual equal.',
        meaningHi: 'विवाह में विलंब अथवा अत्यंत सोच-समझकर निर्णय लेने वाला।'
      },
      {
        label: 'Forked at the start',
        labelHi: 'आरंभ में दो मुंही रेखा',
        meaning: 'Initial period of courtship or separation before permanently settling in marital bliss.',
        meaningHi: 'प्रारंभिक मतभेद के बाद प्रगाढ़ प्रेम व स्थायी सामंजस्य।'
      }
    ],
    keyIndicators: ['Marital Harmony', 'Relationship Timing', 'Devotion', 'Spousal Prosperity'],
    keyIndicatorsHi: ['दांपत्य सुख', 'विवाह का समय', 'प्रेम व समर्पण', 'जीवनसाथी का भाग्य']
  }
];

export const SPECIAL_PALM_SIGNS: SpecialPalmSign[] = [
  {
    id: 'matsya',
    name: 'Matsya Chinha (Fish Sign / मत्स्य चिन्ह)',
    nameHi: 'मत्स्य चिन्ह (Fish Symbol - महालक्ष्मी कृपा)',
    symbolName: 'Fish (मत्स्य)',
    significance: 'One of the highest auspicious marks in Vedic Samudrika Shastra. Bestows monumental wealth, royal status, spiritual liberation, overseas triumphs, and continuous divine protection.',
    significanceHi: 'सामुद्रिक शास्त्र का सर्वश्रेष्ठ शुभ चिन्ह। यह जातक को अपार धन, राजयोग, विदेश में ख्याति, धर्मपरायणता और दीर्घायु प्रदान करता है।',
    rarity: 'Divine Mark',
    mountLocations: 'Mount of Ketu, Mount of Jupiter, Mount of Sun, or Life Line terminus.'
  },
  {
    id: 'trishul',
    name: 'Trishul (Trident / त्रिशूल चिन्ह)',
    nameHi: 'त्रिशूल चिन्ह (Trident - शिव कृपा व विजय)',
    symbolName: 'Trident (त्रिशूल)',
    significance: 'Represents the three Gunas (Sattva, Rajas, Tamas) balanced under divine command. Confers unstoppable victory over obstacles, administrative mastery, and immense authority.',
    significanceHi: 'भगवान शिव का पावन प्रतीक। यह चिन्ह जिस पर्वत पर हो, उसकी शुभता को सौ गुना बढ़ा देता है। जातक समाज में शीर्ष पद पाता है।',
    rarity: 'Extremely Auspicious',
    mountLocations: 'Mount of Jupiter, Mount of Saturn, Mount of Sun, or End of Heart Line.'
  },
  {
    id: 'swastik',
    name: 'Swastika (स्वस्तिक चिन्ह)',
    nameHi: 'स्वस्तिक चिन्ह (Swastika - सर्व कल्याणकारी)',
    symbolName: 'Swastika (स्वस्तिक)',
    significance: 'Sign of purity, untainted fortune, divine protection from evil energies, and deep spiritual insight with worldly riches.',
    significanceHi: 'परम कल्याणकारी चिन्ह। जीवन में किसी भी प्रकार के अमंगल का नाश करता है और चारों दिशाओं से समृद्धि दिलाता है।',
    rarity: 'Divine Mark',
    mountLocations: 'Mount of Jupiter, Mount of Venus, or Center of Palm (Rahu Zone).'
  },
  {
    id: 'lotus',
    name: 'Padma / Lotus (कमल चिन्ह)',
    nameHi: 'कमल चिन्ह (Padma Symbol - साक्षात लक्ष्मी वास)',
    symbolName: 'Lotus (पद्म)',
    significance: 'Guarantees that the native rises from any humble background to immense luxury, beauty, purity of speech, and spotless reputation.',
    significanceHi: 'साक्षात मां लक्ष्मी और सरस्वती का वास। जातक विपरीत परिस्थितियों से उठकर भी शीर्षस्थ ऐश्वर्य व ज्ञान प्राप्त करता है।',
    rarity: 'Divine Mark',
    mountLocations: 'Mount of Jupiter, Mount of Sun, or Mount of Venus.'
  },
  {
    id: 'star',
    name: 'Star (तारा चिन्ह / Star Mark)',
    nameHi: 'तारा चिन्ह (Star Symbol - अप्रत्याशित ख्याति)',
    symbolName: 'Star (तारा)',
    significance: 'Explosive brilliance and sudden fame when present on Sun, Jupiter, or Mercury mounts. Accelerates fortunes overnight.',
    significanceHi: 'सूर्य या गुरु पर्वत पर होने पर रातों-रात ख्याति, पदोन्नति और असाधारण उपलब्धियों का सूचक।',
    rarity: 'Rare',
    mountLocations: 'Mount of Sun, Mount of Jupiter, Mount of Mercury.'
  },
  {
    id: 'square',
    name: 'Square / Raksha Kavach (चतुष्कोण / रक्षा कवच)',
    nameHi: 'चतुष्कोण (Square - ईश्वरीय सुरक्षा कवच)',
    symbolName: 'Square (चतुष्कोण)',
    significance: 'Acts as an impenetrable armor against crises, legal troubles, health setbacks, or loss of reputation.',
    significanceHi: 'सुरक्षा कवच की तरह कार्य करता है। संकट, दुर्घटना अथवा आर्थिक हानि से जातक को सुरक्षित निकालता है।',
    rarity: 'Common',
    mountLocations: 'Over broken lines, Mount of Jupiter, Mount of Saturn, Mount of Mars.'
  },
  {
    id: 'temple',
    name: 'Temple / Flag (ध्वज व मंदिर चिन्ह)',
    nameHi: 'ध्वज एवं मंदिर चिन्ह (Flag & Temple Symbol)',
    symbolName: 'Flag / Temple (ध्वज / मंदिर)',
    significance: 'Indicates unshakeable high moral character, victory in debates/elections, spiritual devotion, and legacy honored for generations.',
    significanceHi: 'धर्मध्वज और विजय का प्रतीक। राजनीति, न्यायपालिका और समाज में शीर्ष कीर्ति और यश की गारंटी।',
    rarity: 'Extremely Auspicious',
    mountLocations: 'Mount of Jupiter, Mount of Sun, Mount of Saturn.'
  }
];

export const HAND_ELEMENTS = [
  {
    element: 'Earth Hand (पृथ्वी हस्त)',
    elementHi: 'पृथ्वी हस्त (स्थिर, व्यावहारिक व कर्मठ)',
    shape: 'Square palm with short, sturdy fingers',
    shapeHi: 'चौकोर हथेली और छोटी, मजबूत उंगलियां',
    traits: 'Grounded, dependable, honest, loves nature, practical, excellent in real estate, engineering, and craftsmanship.',
    traitsHi: 'यथार्थवादी, शांत, मेहनती, प्रकृति प्रेमी, भूमि-भवन और निर्माण कार्यों में कुशल।'
  },
  {
    element: 'Air Hand (वायु हस्त)',
    elementHi: 'वायु हस्त (बुद्धिजीवी, विश्लेषक व विचारक)',
    shape: 'Square palm with long, elegant fingers',
    shapeHi: 'चौकोर हथेली और लंबी, सुडौल उंगलियां',
    traits: 'Intellectual, communicative, curious, analytical, excels in teaching, software, writing, law, and research.',
    traitsHi: 'बौद्धिक, उत्कृष्ट वक्ता, खोजी प्रवृत्ति, तकनीकी, कानून व लेखन में अग्रणी।'
  },
  {
    element: 'Fire Hand (अग्नि हस्त)',
    elementHi: 'अग्नि हस्त (ऊर्जावान, साहसी व महत्वाकांक्षी)',
    shape: 'Rectangular long palm with short, dynamic fingers',
    shapeHi: 'लंबी हथेली और छोटी, फुर्तीली उंगलियां',
    traits: 'Passionate, spontaneous, charismatic leader, high energy, loves adventure, entrepreneurship, and sports.',
    traitsHi: 'ऊर्जा से भरपूर, निडर, साहसी, व्यापारी, नेतृत्वकर्ता और जोखिम लेने में सक्षम।'
  },
  {
    element: 'Water Hand (जल हस्त)',
    elementHi: 'जल हस्त (भावुक, अंतर्ज्ञानी व कलाप्रेमी)',
    shape: 'Rectangular long palm with long, slender fingers',
    shapeHi: 'लंबी हथेली और लंबी, कोमल उंगलियां',
    traits: 'Imaginative, empathetic, psychic intuition, artistic genius, music, healing, psychology, and spiritual depth.',
    traitsHi: 'संवेदनशील, तीव्र अंतर्ज्ञान, कला-संगीत प्रेमी, मनोचिकित्सा और आध्यात्मिक चिंतन।'
  }
];

export interface CustomPalmReadingInput {
  dominantHand: 'right' | 'left';
  gender: 'male' | 'female' | 'other';
  handShape: 'earth' | 'air' | 'fire' | 'water';
  lifeLineType: string;
  headLineType: string;
  heartLineType: string;
  fateLineType: string;
  prominentMounts: string[];
  specialSigns: string[];
}

export function generateVedicPalmReading(input: CustomPalmReadingInput): {
  executiveSummary: string;
  executiveSummaryHi: string;
  vitalityReading: string;
  vitalityReadingHi: string;
  intellectReading: string;
  intellectReadingHi: string;
  heartReading: string;
  heartReadingHi: string;
  careerReading: string;
  careerReadingHi: string;
  mountsReading: string;
  mountsReadingHi: string;
  specialSignsReading: string;
  specialSignsReadingHi: string;
  vedicRemedies: string[];
  vedicRemediesHi: string[];
} {
  const isRight = input.dominantHand === 'right';
  const handDesc = isRight
    ? 'Right Hand (Active Karma Hand - reflects manifest deeds, conscious trajectory, and current destiny)'
    : 'Left Hand (Subconscious / Prarabdha Hand - reflects latent potential, inherited blessings, and inner soul nature)';
  const handDescHi = isRight
    ? 'दायां हाथ (कर्म हाथ - वर्तमान कर्म, पुरुषार्थ और आगामी भविष्य का दर्पण)'
    : 'बायां हाथ (प्रारब्ध हाथ - जन्मजात प्रतिभा, आंतरिक स्वभाव और ईश्वरीय वरदान)';

  const shapeData = HAND_ELEMENTS.find(h => h.element.toLowerCase().includes(input.handShape)) || HAND_ELEMENTS[0];

  return {
    executiveSummary: `Based on Vedic Samudrika Shastra, your ${handDesc} reveals a predominantly ${shapeData.element}. Your palm geometry exhibits harmonic elemental balance with solid Pranic foundation and clear karmic direction.`,
    executiveSummaryHi: `सामुद्रिक शास्त्र के अनुसार, आपका ${handDescHi} मुख्य रूप से ${shapeData.elementHi} के लक्षणों को दर्शाता है। आपकी हथेली की रेखाएं उत्तम ऊर्जा और स्पष्ट जीवन दिशा का संकेत देती हैं।`,
    vitalityReading: `Vitality & Longevity (Jeevan Rekha): Your Life Line indicates strong energetic reserves (Prana Shakti). Daily physical stamina and immune resilience are well supported, providing steady endurance throughout major decades of life.`,
    vitalityReadingHi: `आयु एवं प्राण शक्ति (जीवन रेखा): आपकी जीवन रेखा उत्तम प्राण शक्ति और मजबूत रोग प्रतिरोधक क्षमता का संकेत देती है। जीवन के विभिन्न पड़ावों पर स्वास्थ्य उत्तम रहेगा।`,
    intellectReading: `Intellect & Decision Making (Mastishk Rekha): Your Head Line reflects a sharp mental framework capable of balancing strategic logic with imaginative foresight. You process complex problems calmly without panicking.`,
    intellectReadingHi: `बुद्धि एवं विवेक (मस्तिष्क रेखा): आपकी मस्तिष्क रेखा तीक्ष्ण बौद्धिक क्षमता और निर्णय लेने में स्पष्टता दर्शाती है। आप कठिन परिस्थितियों में भी शांत रहकर उचित निर्णय लेते हैं।`,
    heartReading: `Emotions & Relationships (Hriday Rekha): Your Heart Line indicates deep sincerity and loyalty in personal bonds. You value authentic emotional connection and maintain high integrity in your commitments.`,
    heartReadingHi: `हृदय एवं संबंध (हृदय रेखा): आपकी हृदय रेखा प्रेम व संबंधों में निष्ठा और उदारता को प्रकट करती है। आप रिश्तों को पूरी ईमानदारी और आत्मीयता से निभाते हैं।`,
    careerReading: `Destiny & Wealth (Bhagya Rekha): Your Fate and Sun Line combination reveals steady upward career progress. Self-driven efforts combined with opportune mentorship will unlock key financial breakthroughs.`,
    careerReadingHi: `भाग्य एवं आजीविका (भाग्य रेखा): आपकी भाग्य रेखा कर्मठता और निरंतर उन्नति का योग बनाती है। स्व-प्रयासों और सही मार्गदर्शन से जीवन में प्रचुर धन-समृद्धि का मार्ग प्रशस्त होगा।`,
    mountsReading: `Prominent Mounts: The active development of your ${input.prominentMounts.length > 0 ? input.prominentMounts.join(', ') : 'Jupiter and Venus'} mounts amplifies leadership dignity, personal charisma, and comfort-attracting aura.`,
    mountsReadingHi: `प्रमुख पर्वत: आपके ${input.prominentMounts.length > 0 ? input.prominentMounts.join(', ') : 'बृहस्पति एवं शुक्र'} पर्वत विशेष रूप से बलवान हैं, जो समाज में मान-सम्मान, तेज और सुख-साधनों की प्राप्ति कराते हैं।`,
    specialSignsReading: `Auspicious Vedic Marks: Presence of symbols such as ${input.specialSigns.length > 0 ? input.specialSigns.join(', ') : 'Fish / Trishul / Star'} acts as a divine protective shield, shielding you from sudden crises and catalyzing long-term victory.`,
    specialSignsReadingHi: `शुभ सामुद्रिक चिन्ह: ${input.specialSigns.length > 0 ? input.specialSigns.join(', ') : 'मत्स्य, त्रिशूल व रक्षा कवच'} जैसे शुभ चिन्ह आपकी हथेली में ईश्वरीय कृपा और संकटों से रक्षा का प्रमाण हैं।`,
    vedicRemedies: [
      'Perform daily morning Surya Namaskar and offer Arghya with a copper lota for radiant vitality.',
      'Touch the feet of parents and gurus to continuously empower the Mount of Jupiter.',
      'Apply pure sandalwood or saffron tilak between eyebrows (Ajna Chakra) for mental sharpness.',
      'Feed birds and cows with grains on Wednesdays and Saturdays to harmonize Rahu and Saturn.'
    ],
    vedicRemediesHi: [
      'प्रातःकाल सूर्य देव को तांबे के लोटे से जल अर्पित करें और ॐ सूर्याय नमः का जप करें।',
      'माता-पिता व गुरुजनों का आशीर्वाद लें, जिससे बृहस्पति पर्वत बलवान होता है।',
      'मस्तिष्क को शांत व एकाग्र रखने हेतु चंदन अथवा केसर का तिलक लगाएं।',
      'प्रतिदिन पक्षियों को दाना और गाय को हरा चारा खिलाएं, जिससे ग्रह दोष शांत होते हैं।'
    ]
  };
}
